import React, { useCallback, ReactElement } from 'react';
import { useNavigate } from 'react-router';
import Table from '../../../components/table/table.component';
import TableBody from '../../../components/table-body/table-body.component';
import TableRow from '../../../components/table-row/table-row.component';
import TableCell from '../../../components/table-cell/table-cell.component';
import Widget from '../../../components/widget/widget.component';
import Image from '../../../components/image/image.component';
import FilteredLink from '../../../components/filtered-link/filtered-link.component';
import TextSmallSize from '../../../components/text-small-size/text-small-size.component';
import CompletionBlock from '../../../components/completion-block/completion-block.component';
import FormatService from '../../../services/format-service';
import ScreenService from '../../../services/screen-service';
import useScreenMedium from '../../../hooks/use-screen-medium';
import { ROUTE_DEAL } from '../../../constants/system/routes';
import { QUERY_PARAM_TAB } from '../../../constants/system/query-params';
import { TAB_NAME_DUE_DILIGENCE } from '../../../constants/system/tab-names';
import { WIDGET_NAME_DEALS_IN_DUE_DILIGENCE } from '../../../constants/system/widget-names';
import { DEAL_VIEW } from '../../../constants/entity/permissions';
import AssetDefaultImage from '../../../static/images/asset-default-image.png';

const DashboardWidgetDealsInDueDiligence: ReactMemoComponent = React.memo((
  props: ReactComponentProps,
): ReactElement => {
  const WIDGET_TITLE_DEFAULT: string = 'Deals In Due Diligence';

  const {
    dealListData,
    widgetSettings,
    onWidgetSettingsChange,
  } = props;

  const isMediumScreen: boolean = useScreenMedium();
  const navigate: ReactRouterNavigate = useNavigate();

  const onRowClick: Function = useCallback((dealId: number): void => {
    const withNoSelection: boolean = ScreenService.checkNoSelection();

    if (withNoSelection) {
      navigate({
        pathname: `${ROUTE_DEAL}/${dealId}`,
        search: FormatService.formatQueryParams({
          [QUERY_PARAM_TAB]: TAB_NAME_DUE_DILIGENCE,
        }),
      });
    }
  }, []);

  return (
    <Widget
      isScrollable
      withCustomizeMode
      isEmpty={!dealListData || !dealListData.length}
      widgetName={WIDGET_NAME_DEALS_IN_DUE_DILIGENCE}
      defaultWidgetTitle={WIDGET_TITLE_DEFAULT}
      widgetSettings={widgetSettings}
      onWidgetSettingsChange={onWidgetSettingsChange}
    >
      <Table>
        <TableBody isPlain withZebraRows withHeaderColumn>
          {
            dealListData.map((dealItem: Deal): ReactElement => (
              <TableRow
                isButton
                key={dealItem.id}
                onClick={(): void => {
                  onRowClick(dealItem.id);
                }}
              >
                <TableCell width="5%">
                  <Image
                    text={dealItem.name}
                    link={dealItem.mainPhotoThumbnail || dealItem.mainPhoto || AssetDefaultImage}
                  />
                </TableCell>
                <TableCell width="55%">
                  <div>
                    <FilteredLink link={`${ROUTE_DEAL}/${dealItem.id}`} permission={DEAL_VIEW}>
                      {dealItem.name}
                    </FilteredLink>
                  </div>
                  {
                    !!dealItem.loanAmount && (
                      <TextSmallSize>
                        {`Loan amount: ${dealItem.loanAmount}`}
                      </TextSmallSize>
                    )
                  }
                </TableCell>
                <TableCell width="40%">
                  <CompletionBlock
                    withText
                    withRightOffset={!isMediumScreen}
                    headerText="Due Diligence Completion"
                    itemsTotal={dealItem.tasksTotal}
                    completedItemsTotal={dealItem.tasksCompletedTotal}
                  />
                </TableCell>
              </TableRow>
            ))
          }
        </TableBody>
      </Table>
    </Widget>
  );
});

export default DashboardWidgetDealsInDueDiligence;
